import { createAsyncThunk, createSlice } from "@reduxjs/toolkit"
import { Activation, Entitlement, Sku, License, PointTopup, Product, Redemption, Topup } from "../api/models"
import { addEntityFetchReducers, EntityState } from "./entity"
import { logOut } from "./globalSlice"
import { RootState } from "./store"
import Api from "../api/api"
import { useFetchableState } from "./fetchableState"
import { addPaginatedCollectionReducers, PaginatedCollectionState, usePaginatedCollection } from "./paginatedCollection"

interface RedemptionsState extends PaginatedCollectionState, EntityState<Redemption> {
    // the entitlements the redemption responses side-load (one per code,
    // keyed by entitlement id — a redemption's entitlementIds point here);
    // there is no entitlements slice, and only the Redemptions card reads them
    entitlements: { [id: string]: Entitlement }
    redeemCodeRequestId?: string
    debug: string
}

const initialState: RedemptionsState = {
    ids: undefined,
    link: null,
    currentRequestId: undefined,
    lastFetched: undefined,
    error: undefined,
    entities: {},
    entitlements: {},
    redeemCodeRequestId: undefined,
    debug: "redemptions state",
}

// optional-chained: a deployed backend older than this build omits the array
function absorbEntitlements(state: RedemptionsState, payload: { entitlements?: Entitlement[] }) {
    payload.entitlements?.forEach(entitlement => {
        state.entitlements[entitlement.id] = entitlement
    })
}

export const redemptionsSlice = createSlice({
    name: 'redemption',
    initialState: initialState,
    reducers: {},
    extraReducers(builder) {
        addEntityFetchReducers<
            Redemption,
            { redemption: Redemption },
            { id: string, accessToken: string }
        >(
            builder,
            (payload) => payload.redemption,
            redemptionThunk
        )
        addPaginatedCollectionReducers<
            Redemption,
            { redemptions: Redemption[], link: string | null, skus: Sku[], licenses: License[], topups: Topup[], pointTopups: PointTopup[], products: Product[], activations: Activation[], entitlements: Entitlement[] },
            { reload: boolean, accessToken: string },
            RedemptionsState
        >(
            builder,
            (payload) => payload.redemptions,
            redemptionsThunk,
            redemptionsReducerIntercept
        )
        
        builder.addCase(redeemCodeThunk.pending, (state, action) => {
            if (state.redeemCodeRequestId)
                return
            state.redeemCodeRequestId = action.meta.requestId
        })
        builder.addCase(redeemCodeThunk.fulfilled, (state, action) => {
            if (state.redeemCodeRequestId !== action.meta.requestId)
                return
            state.redeemCodeRequestId = undefined
            state.ids = state.ids ? [action.payload.redemption.id, ...state.ids] : [action.payload.redemption.id]
            state.entities[action.payload.redemption.id] = {
                data: action.payload.redemption,
                lastFetched: Date.now()
            }
            absorbEntitlements(state, action.payload)
        })
        builder.addCase(redeemCodeThunk.rejected, (state, action) => {
            if (state.redeemCodeRequestId !== action.meta.requestId)
                return
            state.redeemCodeRequestId = undefined
        })

        builder.addCase(logOut, (state: RedemptionsState) => {
            state = initialState;
        })

        // the detail fetch's case is registered by addEntityFetchReducers and a
        // second addCase for the same action throws — a matcher (after all the
        // cases, as RTK requires) picks up its side-loaded entitlement
        builder.addMatcher(redemptionThunk.fulfilled.match, (state, action) => {
            absorbEntitlements(state, action.payload)
        })
    }
})


// GET redemptions/:redemptionId

export const redemptionThunk = createAsyncThunk(
    'redemptions/fetchRedemption',
    async ({ id, accessToken }: { id: string, accessToken: string }, { getState, requestId }) => {
        const state = getState() as RootState;   // avoid sending request if it's going to be discarded anyway
        const currentRequestId = state.redemptions.entities[id]?.currentRequestId
        if (currentRequestId !== requestId)
            throw new Error("Request outdated");
        return await Api.getRedemption({ id, accessToken })
    }
)

export function useRedemption(id: string, accessToken: string) {
    return useFetchableState<Redemption>({
        selector: (state: RootState) => state.redemptions.entities[id],
        action: redemptionThunk({ id, accessToken })
    })
}

export function useRedemptions(accessToken: string) {
    return usePaginatedCollection({
        selector: (state: RootState) => state.redemptions,
        moreAction: redemptionsThunk({ reload: false, accessToken }),
        reloadAction: redemptionsThunk({ reload: true, accessToken }),
    })
}

export const redemptionsThunk = createAsyncThunk(
    'redemptions/fetchRedemptions',
    async ({ reload, accessToken }: { reload: boolean, accessToken: string }, { getState, requestId }) => {
        const state = getState() as RootState;   // avoid sending request if it's going to be discarded anyway
        const currentRequestId = state.redemptions.currentRequestId
        if (currentRequestId !== requestId)
            throw new Error("Request outdated");
        // the pending reducer already nulled link on a reload, so this is the
        // first page then and the next one otherwise
        return await Api.listRedemptions({ link: state.redemptions.link, count: 10, accessToken })
    }
)

const redemptionsReducerIntercept = (type: any, state: RedemptionsState, action: any) => {
    if (type === redemptionsThunk.fulfilled) {
        const redemptions: Redemption[] = action.payload.redemptions
        redemptions.forEach(redemption => {
            const entity = state.entities[redemption.id]
            if (entity?.currentRequestId !== undefined)
                return
            state.entities[redemption.id] = {
                data: redemption,
                lastFetched: Date.now()
            }
        })
        absorbEntitlements(state, action.payload)
    }
}

// POST redemptions (redeem code)
export const redeemCodeThunk = createAsyncThunk(
    'redemptions/redeemCode',
    async ({ accessToken, code }: { accessToken: string, code: string }) => {
        return await Api.redeemCode({ code, accessToken })
    }
)

// POST /me/resellers/{reseller}/redemptions (redeem a reseller-distributed
// code — the reseller pages). No redemption row comes back, so this slice has
// nothing to absorb; the licenses/skus/products/topups/library slices react
// to it exactly as to redeemCodeThunk.
export const redeemResellerCodeThunk = createAsyncThunk(
    'redemptions/redeemResellerCode',
    async ({ accessToken, reseller, code }: { accessToken: string, reseller: string, code: string }) => {
        return await Api.redeemResellerCode({ reseller, code, accessToken })
    }
)

export default redemptionsSlice.reducer